<template>
<div class="tool-card pa-5" :class="{ 'is-start-here': startHere }">
  <div class="top-part">
    <span class="icon-part">
      <span class="material-icons-outlined">vpn_key</span>
    </span>
    <span class="name">Keyword Research</span>
    <span v-if="startHere" class="start-here-pill">START HERE</span>
  </div>

  <div class="mt-5">

    <div class="custom-tabs" v-if="availableTabs.length > 1">
      <div
        v-for="(item, idx) in availableTabs"
        :key="idx"
        :class="{ 'is-active': currentTab == item.key }"
        @click="() => { currentTab = item.key }"
      >
        {{ item.label }}
      </div>
    </div>
    <v-text-field outlined dense
      v-model="searchTerm"
      hide-details="auto"
      label="Search"
      color="greenColor"
      v-if="domainLevelCampaignLatestGeneratedReports.length > 0">
    </v-text-field>
  </div>

  <template v-if="currentTab == 'Domain'">
    <div class="flex-grow-1 pages-wrapper mt-3" style="position: relative;">
      <template v-if="domainLevelCampaign">
        <div v-for="report in filteredDomainLevelCampaignLatestGeneratedReports" :key="report.data.id" class="page-item" @click="() => {
          $router.push(reportToRoute(report))
        }">
          <div class="flex-grow-1" style="min-width: 0;">
            
            <div>
              <v-tooltip :disabled="!reportToMainLabelTooltip(report)" top color="black">
                <template v-slot:activator="{ on, attrs }">
                  <span class="name" v-bind="attrs" v-on="on">{{reportToMainLabel(report)}}</span>
                </template>
                <div style="max-width: 350px;">{{ reportToMainLabelTooltip(report) }}</div>
              </v-tooltip>
            </div>

            <div class="time">
              {{reportTypeToLabel(report._type)}}, {{ toLocalDateString(report.data.created_at) }}
            </div>
          </div>
          <div class="action">
            <i class="material-icons-outlined" @click="() => {}">arrow_circle_right</i>
          </div>
        </div>
      </template>
      <div v-if="domainLevelCampaigns.length == 0" class="no-project-block">
        <div>
          <img src="/static/empty-folder.png" />
          <div>
            You haven't created any <br />
            keyword reports for this project yet
          </div>
        </div>
      </div>
    </div>
    <div class="mt-5 d-flex align-center justify-space-between">
      <v-btn v-if="!$store.getters.isDemoUser && !(isWhiteGloveUser && !isMissionControlSession)" rounded small color="profileAnchorColor"
        class="button-with-icon whiteColor--text text-none"
        @click="() => requirePlan(() => $refs.selectKeywordResearchTypeModalRef.showModalAction(), 'keyword')">
        <span>Create new</span>
      </v-btn>
      <span
        v-if="!isWhiteGloveVariant"
        class="how-many"
        @click="() => {
          $router.push(`/sites/${siteId}/campaign/${domainLevelCampaign.id}/silo`)
        }"
      >
        {{ silos.length }} {{ silos.length > 1 ? 'silos' : 'silo' }}
      </span>
    </div>
  </template>

  <template v-else-if="currentTab == 'Silos'">
    <div class="flex-grow-1 pages-wrapper mt-3">
      <template v-if="domainLevelCampaign">
        <div v-for="silo in silos" :key="silo.id" class="page-item" @click="() => {
          $router.push(`/sites/${siteId}/campaign/${domainLevelCampaign.id}/silo?id=${silo.id}`)
        }">
          <div class="flex-grow-1">
            <div class="name">{{ silo.name }}</div>
            <div class="time">
              {{ toLocalDateTimeString(silo.createdAt) }}
            </div>
          </div>
          <div class="action">
            <i class="material-icons-outlined" @click="() => {
              $router.push(`/sites/${siteId}/campaign/${domainLevelCampaign.id}/silo?id=${silo.id}`)
            }">arrow_circle_right</i>
          </div>
        </div>
      </template>
    </div>
    <div class="mt-5 d-flex align-center justify-space-between">
      <div></div>
      <span
        v-if="!isWhiteGloveVariant"
        class="how-many"
        @click="() => {
          $router.push(`/sites/${siteId}/campaign/${domainLevelCampaign.id}/silo`)
        }"
      >
        {{ silos.length }} {{ silos.length > 1 ? 'silos' : 'silo' }}
      </span>
    </div>
  </template>

  <select-keyword-research-type-modal
    ref="selectKeywordResearchTypeModalRef"
    :key="domainLevelCampaign?.id"
    :currentCampaign="domainLevelCampaign"
  ></select-keyword-research-type-modal>

</div>
</template>

<script>
import { mixin } from '@/components/mixin'
import { getPureServicePkgName } from '@/utils'
import { SUBSCRIPTIONS_WITH_ACCESS_TO_KEYWORD_RESEARCH_TOOLS, whiteGlovePlans } from "@/components/plans_constants";

import SelectKeywordResearchTypeModal from '@/components/SitesSingleView/Tools/SelectKeywordResearchTypeModal.vue'

import { startHereMixin } from './start-here'
import { planGateMixin } from '@/components/Auth/plan-gate'

export default {
  mixins: [mixin, startHereMixin, planGateMixin],
  components: {
    SelectKeywordResearchTypeModal,
  },
  props: {
    isWhiteGloveVariant: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    isSpecialAdminUser () {
      return this.$store.getters.isSpecialAdminUser
    },
    isSpecialOwnerSelected () {
      return this.$store.getters.isSpecialOwnerSelected
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    siteId () {
      return parseInt(this.$route.params.siteId)
    },
    filteredDomainLevelCampaignLatestGeneratedReports() {
      return this.domainLevelCampaignLatestGeneratedReports.filter(report => (
        this.reportToMainLabel(report).toLowerCase().includes(this.searchTerm.toLowerCase())
        || this.reportTypeToLabel(report._type).toLowerCase().includes(this.searchTerm.toLowerCase())
      ))
    },
    domainLevelCampaigns () {
      return this.campaigns.filter(item => item.isDomainLevelCampaign)
    },
    domainLevelCampaign () {
      if (this.domainLevelCampaigns.length > 0) {
        return this.domainLevelCampaigns[0]
      }
      return null
    },
    availableTabs () {
      const tabs = [
        { key: 'Domain', label: 'Keyword Research tools' },
        { key: 'Silos', label: 'Your Silos' },
      ]
      if (this.isWhiteGloveVariant) {
        return tabs.filter((t) => t.key !== 'Silos')
      }
      return tabs
    },
  },
  watch: {
    isWhiteGloveVariant (v) {
      if (v && this.currentTab === 'Silos') {
        this.currentTab = 'Domain'
      }
    },
    siteId: function () {
      this.loadCampaigns()
    },
    '$store.state.selectedSubAccountRelation': function (newVal, oldVal) {
      const newId = newVal && typeof newVal === 'object' ? newVal.id : null
      const oldId = oldVal && typeof oldVal === 'object' ? oldVal.id : null
      if (newId !== oldId) {
        this.loadCampaigns()
      }
    },
    '$store.state.triggerRefreshKeywordResearchCard': function () {
      this.loadCampaigns()
    },
  },
  data () {
    return {
      currentTab: 'Domain',

      campaigns: [],

      searchTerm: '',

      domainLevelCampaignLatestGeneratedReports: [],
      silos: [],

      campaignsLoadGen: 0,
      reportsLoadGen: 0,
      silosLoadGen: 0,
    }
  },
  methods: {
    syncReportsAndSilosAfterCampaigns () {
      const dc = this.domainLevelCampaign
      if (!dc) {
        this.domainLevelCampaignLatestGeneratedReports = []
        this.silos = []
        return
      }
      this.domainLevelCampaignLatestGeneratedReports = []
      this.silos = []
      this.loadCampaignLatestGeneratedReports(dc.id)
      this.loadSilos(dc.id)
    },
    loadCampaigns () {
      const siteId = this.siteId
      this.campaignsLoadGen += 1
      const gen = this.campaignsLoadGen
      this.$store.dispatch('getCampaignsForSite', siteId)
        .then(response => {
          if (gen !== this.campaignsLoadGen || siteId !== this.siteId) {
            return
          }
          if (response.data && response.data.status == 'SUCCESS') {
            this.campaigns = response.data.campaigns
            this.syncReportsAndSilosAfterCampaigns()
          }
        })
        .catch(() => {
          if (gen !== this.campaignsLoadGen || siteId !== this.siteId) {
            return
          }
        })
    },
    loadCampaignLatestGeneratedReports (campaignId) {
      const siteId = this.siteId
      this.reportsLoadGen += 1
      const gen = this.reportsLoadGen
      this.$store.dispatch('getCampaignLatestGeneratedReports', campaignId)
        .then(response => {
          if (gen !== this.reportsLoadGen || siteId !== this.siteId) {
            return
          }
          if (this.domainLevelCampaign?.id !== campaignId) {
            return
          }
          if (response.data && response.data.status == 'SUCCESS') {
            this.domainLevelCampaignLatestGeneratedReports = response.data.result?.length
              ? response.data.result
              : []
          }
        })
        .catch(() => {
          if (gen !== this.reportsLoadGen || siteId !== this.siteId) {
            return
          }
          if (this.domainLevelCampaign?.id !== campaignId) {
            return
          }
        })
    },
    loadSilos (campaignId) {
      const siteId = this.siteId
      this.silosLoadGen += 1
      const gen = this.silosLoadGen
      // only silo id / name / createdAt are rendered here
      this.$store.dispatch('getSilos', {
        campaignId,
        paginate: 0,
        includeKeywords: 0,
        includeAiAndPageRunResult: 0,
        includeContentReportOrder: 0,
        includePage: 0,
      })
        .then((response) => {
          if (gen !== this.silosLoadGen || siteId !== this.siteId) {
            return
          }
          if (this.domainLevelCampaign?.id !== campaignId) {
            return
          }
          const data = response.data && response.data.data
          this.silos = Array.isArray(data) ? data : []
        })
        .catch(() => {
          if (gen !== this.silosLoadGen || siteId !== this.siteId) {
            return
          }
          if (this.domainLevelCampaign?.id !== campaignId) {
            return
          }
        })
    },
    reportToMainLabel (report) {
      if (report._type === 'KeywordResearch') {
        const keywords = report.data.keywords || []
        if (keywords.length) {
          let label = keywords[0]
          if (keywords.length > 1) {
            label += ` (+${keywords.length - 1} keywords)`
          }
          return label
        }
      } else if ([
        'TopLevelSupportKeywordResearch',
        'SupportKeywordResearch',
      ].includes(report._type)) {
        const topics = report.data.topics || []
        if (topics.length) {
          let label = topics[0] || '-'
          if (topics.length > 1) {
            label += ` (+${topics.length - 1} topics)`
          }
          return label
        }
      } else if (report._type === 'SiloReport') {
        if (report.data?.name) {
          return report.data?.name
        }
      } else if (report._type === 'SiloResearch.OneClickSilo') {
        if (report.data?.topic) {
          return report.data?.topic
        }
      } else if (report._type === 'SiloResearch.CustomSilo') {
        const topics = report.data.topics || []
        if (topics.length) {
          let label = topics[0] || '-'
          if (topics.length > 1) {
            label += ` (+${topics.length - 1})`
          }
          return label
        }
      }

      // fallback to report type label
      return this.reportTypeToLabel(report._type)
    },
    reportToMainLabelTooltip (report) {
      if (report._type === 'KeywordResearch') {
        if ((report?.data?.keywords?.length || 0) > 1) {
          return report.data.keywords.join(', ')
        }
      } else if ([
        'TopLevelSupportKeywordResearch',
        'SupportKeywordResearch',
        'SiloResearch.CustomSilo',
      ].includes(report._type)) {
        if (report.data.topics.length > 1) {
          return report.data.topics.join(', ')
        }
      }

      return ''
    },
    reportTypeToLabel (reportType) {
      const accountType = this.selectedSubAccountRelation?.ownerAccountType || this.user?.accountType
      
      // NOTE: this should be removed once more keyword research tools are open to users
      const trimKeywordStrategyLabel = SUBSCRIPTIONS_WITH_ACCESS_TO_KEYWORD_RESEARCH_TOOLS.map(x => getPureServicePkgName(x)).includes(getPureServicePkgName(accountType))
      
      return ({
        'KeywordResearch': 'Keyword Insight',
        'SupportKeywordResearch': 'Keyword strategy - Existing content - Supporting keywords',
        'KeywordWizardResearch:PAGE_OPPORTUNITY': 'Keyword strategy - Existing content - Pages',
        'KeywordWizardResearch:SITE_ONLY_EXIST': 'Keyword strategy - Existing content - Top-level keywords',
        'TopLevelSupportKeywordResearch': trimKeywordStrategyLabel? 'Keyword strategy' : 'Keyword strategy - New content',
        'SiloReport': this.isWhiteGloveUser ? 'Content Plan' : 'Silo Report',
        'SiloResearch.OneClickSilo': '1-Click Silo',
        'SiloResearch.CustomSilo': 'Custom Silo',
      })[reportType] || reportType
    },
    reportToRoute (report) {
      return ({
        'KeywordResearch': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-insight/${report.data.id}`,
        'SupportKeywordResearch': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-wizard/existing-content/supporting-keyword-research`,
        'KeywordWizardResearch:PAGE_OPPORTUNITY': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-strategy/existing-content/page-opportunity`,
        'KeywordWizardResearch:SITE_ONLY_EXIST': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-strategy/existing-content/site-only`,
        'TopLevelSupportKeywordResearch': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-wizard/new-content/keyword-only`,
        'SiloReport': this.isWhiteGloveUser ? `/sites/${ this.siteId }/content-plans/${report.data.id}` : `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/silo?id=${report.data.id}`,
        'SiloResearch.OneClickSilo': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-strategy/one-click-silo/${report.data.id}`,
        'SiloResearch.CustomSilo': `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-strategy/custom-silo/${report.data.id}`,
      })[report._type] || `/sites/${ this.siteId }/campaign/${ this.domainLevelCampaign.id }/keyword-insight`  // default
    },
  },
  mounted () {
    this.loadCampaigns()    
  }
}
</script>

<style lang="scss" scoped>
.custom-tabs {
  display: flex;
  background: var(--v-gray7Color-base);
  border-radius: 10px;
  padding: 3px;
  & > div {
    flex-basis: 35%;
    text-align: center;
    padding: 10px 10px;
    border-radius: 10px;
    cursor: pointer;
    color: var(--v-mainColor-base);
    font-weight: 700;

    &:first-child {
      flex: 1;
    }

    &.is-active {
      background: var(--v-whiteColor-base);
      box-shadow: 0px 3px 4px -1px #00000014;
    }
  }
}

.white-glove-site-content-audit-oboarding-tip {
  position: absolute;
  top: 32px;
  right: 40px;
}
</style>
